/**
 * Vertrag Modul „Verwaltung" (#811, Durchstich zu SPEC docs/module/verwaltung/SPEC.md): `/api/verwaltung/v1/*`.
 * Quelle der Wahrheit für Konten/Gruppen bleibt der Identitätsdienst; die API nennt ihn nie beim Namen.
 */
import type { PortalRole } from './api.js';
import type { ModuleKey, ModulFreischaltung, Stufe } from './modules.js';

export const VERWALTUNG_API = '/api/verwaltung/v1';

/** Pflicht-Domain für Einladungen ins Team */
export const TEAM_EMAIL_DOMAIN = 'parfuemerie-brueckner.com';

export type MitarbeiterStatus = 'aktiv' | 'eingeladen' | 'deaktiviert';

export interface Mitarbeiter {
  /** stabile ID (Konto-PK als String) */
  id: string;
  name: string;
  email: string;
  status: MitarbeiterStatus;
  rollen: PortalRole[];
  module: ModulFreischaltung;
  /** angemeldete Person selbst */
  istSelbst: boolean;
  /** Superadmin/technisches Konto: nur Anzeige, keine Änderung */
  geschuetzt: boolean;
  letzteAnmeldung: string | null;
  /** offene Vier-Augen-Anträge zu dieser Person */
  freigaben: Freigabe[];
}

export interface ModulInfo {
  key: ModuleKey;
  label: string;
  /** Label des Bereichs aus `AREAS` (#819, z. B. „Kundenportal“, „CRM“) */
  bereich: string;
  stufen: Stufe[];
}

export interface RolleInfo {
  key: PortalRole;
  label: string;
}

export interface MitarbeiterListe {
  items: Mitarbeiter[];
  module: ModulInfo[];
  rollen: RolleInfo[];
  /** Vorbelegung der Module je Rolle (nur Vorschlag beim Einladen) */
  vorbelegung: Record<PortalRole, ModulFreischaltung>;
  /** Vier-Augen eingeschaltet und wirksam (≥ 2 Verwalter) */
  vierAugen: { aktiv: boolean; eingeschaltet: boolean; verwalter: number };
  /** offene Anträge, die die angemeldete Person bestätigen darf */
  offeneFreigaben: Freigabe[];
}

export interface EinladenRequest {
  email: string;
  name: string;
  rolle: PortalRole;
  /** optional abweichend von der Vorbelegung der Rolle */
  module?: ModulFreischaltung;
}

export interface EinladenResponse {
  mitarbeiter: Mitarbeiter;
  einladung: { gueltigBis: string; versand: 'versendet' | 'fehlgeschlagen' };
  /** Teile, die auf Vier-Augen-Bestätigung warten */
  freigaben: Freigabe[];
}

export interface ModulSetzenRequest {
  modul: ModuleKey;
  /** null = entziehen */
  stufe: Stufe | null;
}

/** 200: sofort wirksam; 202: wartet auf Bestätigung */
export type ModulSetzenResponse = { status: 'ausgefuehrt'; mitarbeiter: Mitarbeiter } | { status: 'wartet_freigabe'; freigabe: Freigabe };

export interface DeaktivierenRequest {
  grund?: string;
}

export type FreigabeStatus = 'wartet_freigabe' | 'ausgefuehrt' | 'abgelehnt' | 'abgelaufen';

export interface Freigabe {
  id: string;
  mitarbeiterId: string;
  mitarbeiterName: string;
  /** Modul-Stufe (`verwaltung:verwalten`) oder Rolle `gf` */
  art: 'modul' | 'rolle';
  modul: ModuleKey | null;
  stufe: Stufe | null;
  rolle: PortalRole | null;
  antragstellerName: string;
  /** Antragsteller = angemeldete Person → darf nicht selbst bestätigen */
  eigenerAntrag: boolean;
  erstelltAm: string;
  gueltigBis: string;
  status: FreigabeStatus;
}

/** Fehlerformat (MODUL-VERTRAG § 3): `code` stabil, `message` deutsch für die Anzeige */
export interface VerwaltungFehler {
  statusCode: number;
  code: string;
  message: string;
}

export const ROLLEN_LABEL: Record<PortalRole, string> = {
  gf: 'Geschäftsführung',
  office: 'Büro',
  marketing: 'Marketing',
  lager: 'Lager',
  lager_lead: 'Lagerleitung',
  ops: 'Technik/Betrieb',
  readonly: 'Nur lesen',
  moderator: 'Moderation',
};

export const STUFEN_LABEL: Record<Stufe, string> = { lesen: 'Lesen', bearbeiten: 'Bearbeiten', verwalten: 'Verwalten' };
